import _ from 'lodash'
import type { AppUnitType } from '../../../shared/domain/units/types.no-deps'
import { sortedUnitTypes } from '../../src/utils/unitTypes'
import type { Type_IUnit } from '../Type/Type.hive'
import type { UA_AddUnitsData, UA_IFilterUnit } from './UA.types'

// This method attempts to work from the list of units provided and figure out all the parents and
// related unit Type. It sets up various bits of state that are used by the addUnits page to
// hierarchically select units.
export function UA_readyFilterItems(
    addUnitsData: UA_AddUnitsData,
    selectedUnits: Type_IUnit[],
    allUnits: Type_IUnit[],
    allUnitTypes: AppUnitType[]
): UA_AddUnitsData {
    // Create parent look up
    const unitParentLookup = allUnits.reduce<{ [key: string]: number[] }>((acc, current) => {
        current.children.forEach((childId) => {
            if (acc[childId]) {
                acc[childId].push(current.id)
            } else {
                acc[childId] = [current.id]
            }
        })

        return acc
    }, {})

    const andParents = (ids: number[]): number[] => {
        const parents = _(ids)
            .map((id) => unitParentLookup[id])
            .filter((entry) => entry !== undefined)
            .flatten()
            .uniq()
            .value()

        if (parents.length) {
            return ids.concat(andParents(parents))
        } else {
            return ids
        }
    }

    // Find all ids of the provided unit and all their parents
    const unitIds = new Set(andParents(selectedUnits.map((unit) => unit.id)))

    // Extract all the units that we're interested in
    const units = allUnits.filter((unit) => unitIds.has(unit.id))

    const filterUnits: UA_IFilterUnit[] = units.map((unit) => ({
        unitId: unit.id,
        unitTypeId: unit.type,
        selected: false,
    }))

    // Create a lookup from filterUnit id to filterUnit
    const filterUnitsLookup = new Map<number, UA_IFilterUnit>(
        filterUnits.map((filterUnit): [number, UA_IFilterUnit] => [filterUnit.unitId, filterUnit])
    )

    // Find all the unit types for the unit hiearchy we're dealing with
    const unitTypeIds = _.uniq(units.map((unit) => unit.type))

    const unitTypes = allUnitTypes.filter((unitType) => unitTypeIds.includes(unitType.id))

    const orderedUnitTypes = sortedUnitTypes(unitTypes, allUnitTypes)

    // Finally create a lookup from unit type to filter units so that we know what units to display
    // based on the unit type that we currently want to display. The 'addUnits' screen works by
    // displaying only one unit type at a time.
    const filterUnitsByUnitTypeId = filterUnits.reduce((acc, filterUnit) => {
        const list = acc.get(filterUnit.unitTypeId)
        if (list) {
            list.push(filterUnit.unitId)
        } else {
            acc.set(filterUnit.unitTypeId, [filterUnit.unitId])
        }
        return acc
    }, new Map<number, number[]>())

    return {
        ...addUnitsData,
        filterUnitsLookup,
        orderedUnitTypes,
        filterUnitsByUnitTypeId,
    }
}
